/**
 * Firestore path helpers for project documents.
 *
 * Deliberately free of project model/report imports: a repository that needs nothing but a
 * project path (e.g. `TimeTrackingRepository`) would otherwise pull the whole project graph in
 * behind it. `ProjectRepository` re-exports these, so existing callers are unaffected.
 */

import { getClientRef } from "./ClientRepository";

/**
 * A leak detection project keeps its documents in its own collection, and its id ends in `ld`.
 * Same rule as Cloud's `getIsLeakDetectionProject`.
 */
export function isLeakDetectionProject(projectId: string): boolean {
  return projectId.endsWith("ld");
}

export function isDryingProject(projectId: string): boolean {
  return !projectId.endsWith("ld");
}

/**
 * The collection holding a project's document. Flex project ids (`…_flex`) never end in `ld`,
 * so they resolve to `projects`, matching Cloud's `getProjectCollection`.
 */
export function getProjectCollectionRef(projectId: string): string {
  return (
    getClientRef() +
    "/" +
    (isLeakDetectionProject(projectId) ? "leakDetectionProjects" : "projects")
  );
}

export function getProjectRef(projectId: string): string {
  return getProjectCollectionRef(projectId) + "/" + projectId;
}

function getArchivedProjectCollectionRef(): string {
  return getClientRef() + "/archivedProjects";
}

/**
 * Path of a project's archival counterpart. Both archiving and deleting a project move it here
 * (a delete is an archive plus `deleted: true`), together with a copy of every project
 * subcollection, so this is where a no-longer-live project's data is read from.
 */
export function getArchivedProjectRef(projectId: string): string {
  return getArchivedProjectCollectionRef() + "/" + projectId;
}
